import React from 'react';
import PropTypes from 'prop-types';

import Button from '@decisiv/ui-components/lib/components/Button';

import FilterMenu from './FilterMenu';
import { FilterBar, FilterBarSpacer } from './styles';

/**
 * The users list's filter row (PD-4063).
 *
 * The mockup's fourth filter, All Teams, is deliberately absent — teams do not
 * exist until PD-4213.
 *
 * CLEAR ALL only appears once something is filtered; the design keeps its frame
 * hidden otherwise.
 */
function UsersFilters({
  accountOptions,
  accountsTotal,
  filters,
  hasMoreAccounts,
  isLoadingAccounts,
  jobRoleOptions,
  onChange,
  onLoadMoreAccounts,
  onSearchAccounts,
  permissionOptions,
}) {
  const hasFilters = Object.values(filters).some((values) => values.length > 0);

  const setFilter = (key) => (values) =>
    onChange({ ...filters, [key]: values });

  return (
    <FilterBar>
      {/* The one paged menu: the segment can hold hundreds of accounts, so this
          loads the next page as the list is scrolled (PD-4577). Permissions and
          Job Role are bounded lists held whole. */}
      <FilterMenu
        searchable
        hasMore={hasMoreAccounts}
        isLoading={isLoadingAccounts}
        label="All Accounts"
        options={accountOptions}
        searchPlaceholder="Search accounts"
        selected={filters.accountIds}
        title="Accounts"
        total={accountsTotal}
        onApply={setFilter('accountIds')}
        onLoadMore={onLoadMoreAccounts}
        onSearch={onSearchAccounts}
      />
      {/* Permissions is the one menu with an "All" row, and the one the design
          gives no search input. */}
      <FilterMenu
        allOption
        label="All Permissions"
        options={permissionOptions}
        selected={filters.permissions}
        title="Permissions"
        onApply={setFilter('permissions')}
      />
      <FilterMenu
        searchable
        label="All Job Roles"
        options={jobRoleOptions}
        searchPlaceholder="Search job roles"
        selected={filters.jobRoles}
        title="Job Role"
        onApply={setFilter('jobRoles')}
      />
      <FilterBarSpacer />
      {hasFilters && (
        <Button
          kind="secondary"
          size="small"
          text="Clear All"
          onClick={() =>
            onChange({ accountIds: [], permissions: [], jobRoles: [] })
          }
        />
      )}
    </FilterBar>
  );
}

export const FILTER_VALUES = PropTypes.shape({
  accountIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  permissions: PropTypes.arrayOf(PropTypes.string),
  jobRoles: PropTypes.arrayOf(PropTypes.string),
});

const OPTIONS = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
);

UsersFilters.propTypes = {
  accountOptions: OPTIONS,
  /** How many accounts match, loaded or not. */
  accountsTotal: PropTypes.number,
  filters: FILTER_VALUES.isRequired,
  hasMoreAccounts: PropTypes.bool,
  isLoadingAccounts: PropTypes.bool,
  jobRoleOptions: OPTIONS,
  onChange: PropTypes.func.isRequired,
  onLoadMoreAccounts: PropTypes.func.isRequired,
  onSearchAccounts: PropTypes.func.isRequired,
  permissionOptions: OPTIONS,
};

UsersFilters.defaultProps = {
  accountOptions: [],
  accountsTotal: 0,
  hasMoreAccounts: false,
  isLoadingAccounts: false,
  jobRoleOptions: [],
  permissionOptions: [],
};

export default UsersFilters;
